import { useEffect, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command'
import { Check, ChevronsUpDown, CirclePlus, X } from 'lucide-react'
import { Separator } from '@components/ui/separator'
import { cn } from '@/utils'

const DEFAULT_KEYWORDS = ['Arts', 'Band', 'Baseball', 'Choir', 'Football']

interface SearchKeywordComboBoxProps {
  showAddButton: boolean
  placeholder?: string
  /** Available keywords to choose from. Defaults to the pre-existing generic list so callers that
   *  don't pass one (e.g. `TemplatesPanel`, `BackgroundsPanel`) are unaffected. */
  keywords?: string[]
  /** Controlled selected keywords. Falls back to internal state when omitted. */
  value?: string[]
  onValueChange?: (selected: string[]) => void
  /** Message shown when the typed text matches nothing. Defaults to the pre-existing generic
   *  copy; ClikArt passes "No matching keywords" per WMPCM-10954 AC-3. */
  emptyMessage?: string
  /** Reports open/close so a host panel can track "is one of my dropdowns open" (e.g. for an
   *  Escape-key priority chain) without owning this component's internal state. */
  onOpenChange?: (open: boolean) => void
  /** Extra classes for the selected-keyword badge row (e.g. a panel's own spacing from its Figma). */
  badgesClassName?: string
}

export function SearchKeywordComboBox({
  showAddButton,
  placeholder,
  keywords: keywordsProp,
  value,
  onValueChange,
  emptyMessage = 'No results found.',
  onOpenChange,
  badgesClassName,
}: SearchKeywordComboBoxProps) {
  const [open, setOpenState] = useState(false)
  const setOpen = (next: boolean) => {
    setOpenState(next)
    onOpenChange?.(next)
  }
  const [keywords, setKeywords] = useState(keywordsProp ?? DEFAULT_KEYWORDS)

  // `keywordsProp` is often still empty/unresolved on first mount (e.g. ClikArt's keyword list
  // is a separate async query that hasn't landed yet) — `useState`'s initializer only runs once,
  // so without this the combobox would be stuck offering nothing to search once the real list
  // arrives. Re-syncs whenever a controlled list is actually provided; callers that never pass
  // `keywordsProp` (relying on DEFAULT_KEYWORDS + local "Add new" growth) are unaffected.
  useEffect(() => {
    if (keywordsProp !== undefined) setKeywords(keywordsProp)
  }, [keywordsProp])
  const [localSelected, setLocalSelected] = useState<string[]>([])
  const selected = value ?? localSelected
  const [search, setSearch] = useState('')

  function setSelected(next: string[]) {
    setLocalSelected(next)
    onValueChange?.(next)
  }

  const filtered = keywords.filter((kw) => kw.toLowerCase().includes(search.toLowerCase()))
  const showAddNew = !selected.some((kw) => kw.toLowerCase() === search.toLowerCase())

  // WMPCM-10954 AC-4/AC-7: toggling a keyword must NOT clear the typed filter text or close the
  // dropdown — multiple keywords are picked one after another against the same filtered list.
  function handleSelect(kw: string) {
    setSelected(selected.includes(kw) ? selected.filter((t) => t !== kw) : [...selected, kw])
    setOpen(true)
  }

  function handleAddNew() {
    const name = window.prompt('Enter new topic name', search.trim())

    if (
      name &&
      name.trim() !== '' &&
      !keywords.some((kw) => kw.toLowerCase() === name.toLowerCase())
    ) {
      setKeywords((prev) => [...prev, name])
      setSearch('') // Clear to show updated full list
      setOpen(true)
    }
  }

  function handleRemove(kw: string) {
    setSelected(selected.filter((t) => t !== kw))
  }

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {/* WMPCM-11550: the Figma design shows this as a select (chevron, like the Category
           *  dropdown), not a text input. Rendering it with a search icon made the open state show
           *  two stacked search fields (this trigger + the popover's `CommandInput`), so the
           *  trigger is now a plain select-style button and the popover owns the only search. */}
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={placeholder ?? 'Search by keywords'}
            className="w-[var(--panel-dropdown-width)] h-9 px-3 flex justify-between items-center rounded-md border bg-card text-sm text-left focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none transition-[color,box-shadow]"
          >
            <span className="truncate text-muted-foreground">
              {placeholder ?? 'Search by keywords'}
            </span>
            <ChevronsUpDown className="ml-2 opacity-50 shrink-0 size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--panel-dropdown-width)] p-0 mt-2" align="start">
          <Command>
            <CommandInput
              placeholder="Search"
              value={search}
              onValueChange={setSearch}
              className="h-9"
              autoFocus
            />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {filtered.map((kw) => (
                  <CommandItem key={kw} value={kw} onSelect={() => handleSelect(kw)}>
                    <span className="flex-1 text-left">{kw}</span>
                    {selected.includes(kw) && (
                      <Check className="ml-2 h-4 w-4 text-muted-foreground" />
                    )}
                  </CommandItem>
                ))}
                {showAddButton && showAddNew && (
                  <>
                    <Separator />
                    <CommandItem
                      key="add-new"
                      value={search}
                      className="text-muted-foreground flex items-center"
                      onSelect={handleAddNew}
                    >
                      <CirclePlus className="w-1 h-1 " />
                      <span className="flex-1 text-left text-foreground">Add new</span>
                    </CommandItem>
                  </>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {/* Badges below (not above) */}
      {selected.length > 0 && (
        <div className={cn('mt-2 flex flex-wrap items-center gap-2', badgesClassName)}>
          {selected.map((kw) => (
            <span
              key={kw}
              className="inline-flex h-5 items-center justify-center gap-1 rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground"
            >
              {kw}
              <button
                onClick={() => handleRemove(kw)}
                className="flex items-center"
                type="button"
                aria-label="Remove keyword"
              >
                <X className="w-3 h-3 text-muted-foreground hover:text-foreground" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
